import { isEmpty } from 'lodash'
import PropTypes from 'prop-types'
import { useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { connect } from 'react-redux'
// eslint-disable-next-line no-restricted-imports
import { useHistory, useLocation, useParams, withRouter } from 'react-router-dom'
import { usePrevious } from 'react-use'
import { bindActionCreators } from 'redux'
import { object, string } from 'yup'

import Button from '@/components/Button'
import Form, { Text } from '@/components/Form'
import yupTestContextHelper from '@/components/Form/yupTestContextHelper'
import KeyValue from '@/components/KeyValue'
import Page from '@/components/Page'
import { fetchRoles, fetchUsersPaginated, resetRequest } from '@/store/actions'
import { ADD_MEMBER_TO_SPACE } from '@/store/actionTypes'
import {
  getCurrentUser,
  getSpaceAdminRole,
  getSpaces,
  isRequestComplete,
  isRequestError,
  isRequestPending
} from '@/store/selectors'

import {
  addSpaceAdmin,
  addSpaceAndSpaceAdmin,
  addUserToSpaceAsAdmin,
  fetchSpaceMembersInRole,
  removeSpaceAdmin,
  updateSpace
} from './actions'
import {
  ADD_SPACE,
  ADD_SPACE_ADMIN,
  FETCH_ORG_ADMINS_TO_ADD,
  FETCH_SPACE_MEMBERS,
  REMOVE_SPACE_ADMIN,
  UPDATE_SPACE
} from './actionTypes'
import messages from './messages'
import { getSearchedUsersNotAlreadyAdmins, getSpaceAdmins, makeGetSpaceById } from './selectors'
import SpaceAdmins from './SpaceAdmins'

const errorsToHandle = [
  {
    type: ADD_SPACE,
    data: { detailCode: 'OSEPAS' }
  },
  {
    type: UPDATE_SPACE,
    data: { detailCode: 'OSEPUS' }
  },
  {
    type: ADD_MEMBER_TO_SPACE,
    data: { detailCode: 'OSEPMS' }
  },
  {
    type: ADD_SPACE_ADMIN,
    data: { detailCode: 'OSEPASA' }
  },
  {
    type: REMOVE_SPACE_ADMIN,
    data: { detailCode: 'OSEPRRSA' }
  }
]

function OrganizationSpaceEditPage(props: any) {
  const intl = useIntl()

  const history = useHistory()
  const location = useLocation()
  const params = useParams<{ spaceId?: string }>()

  const schema = object().shape({
    name: string()
      .trim()
      // @ts-expect-error
      .required(<FormattedMessage {...messages.requiredError} />)
      .test(
        'isUnique',
        // @ts-expect-error
        <FormattedMessage {...messages.spaceNameAlreadyExists} />,
        yupTestContextHelper(
          (value: any) =>
            !props.spaces.some((space: any) => {
              return space.name.toLowerCase() === value.toLowerCase() && space.id !== props.space.id
            })
        )
      ),
    description: string().trim()
  })

  // componentDidMount
  useEffect(() => {
    props.actions.resetRequest([ADD_SPACE, ADD_MEMBER_TO_SPACE, ADD_SPACE_ADMIN, UPDATE_SPACE, REMOVE_SPACE_ADMIN])
    props.actions.fetchRoles()

    if (props.space && !isEmpty(props.space)) {
      loadSpaceMembers()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // componentDidUpdate
  const prevProps = usePrevious(props)
  useEffect(() => {
    if (!prevProps) return

    if (!prevProps.isUpdateComplete && props.isUpdateComplete && !props.hasError) {
      goBack()
    }

    if (prevProps.space !== props.space) {
      loadSpaceMembers()
    }

    // Wait till the last action in the chain is complete before returning
    if (
      !props.hasError &&
      props.isAddSpaceComplete &&
      props.isAddMemberToSpaceComplete &&
      props.isAddSpaceAdminComplete &&
      !prevProps.isAddSpaceAdminComplete
    ) {
      goBack()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props, prevProps])

  const loadSpaceMembers = () => {
    const { space, spaceAdminRole } = props
    props.actions.fetchSpaceMembersInRole(space.id, spaceAdminRole)
  }

  const stripPath = (path: any, count: any) => {
    for (let i = 0; i < count; i++) {
      path = path.slice(0, Math.max(0, path.lastIndexOf('/')))
    }

    return path
  }

  const goBack = () => {
    const targetPath = stripPath(location.pathname, 1)
    history.push(targetPath)
  }

  const onSubmit = (values: any) => {
    if (params.spaceId) {
      updateSpace({ ...values })
    } else {
      createSpace({ ...values })
    }
  }

  const createSpace = (newSpace: any) => {
    props.actions.addSpaceAndSpaceAdmin(newSpace, props.currentUser, props.spaceAdminRole.id)
  }

  const updateSpace = (updatedSpace: any) => {
    props.actions.updateSpace(props.space.id, updatedSpace)
  }

  const { isSaving, isAddingSpaceAdmin, space } = props
  const title = params.spaceId ? { ...messages.editSpace } : { ...messages.createSpace }
  const actionMessage = isAddingSpaceAdmin ? (
    <FormattedMessage {...messages.addingRole} />
  ) : params.spaceId ? (
    <FormattedMessage {...messages.updatingSpace} />
  ) : (
    <FormattedMessage {...messages.creatingSpace} />
  )

  return (
    <Page errorsToHandle={errorsToHandle}>
      <Page.Header
        title={intl.formatMessage(title)}
        goBack={goBack}
        backNavTitle={<FormattedMessage {...messages.back} />}
      />
      <>
        {((!!params.spaceId && !isEmpty(space)) || !params.spaceId) && (
          <Form
            initialValues={{ name: space.name || '', description: space.description || '' }}
            isActionInProgress={isSaving || (!params.spaceId && isAddingSpaceAdmin)}
            actionMessage={actionMessage}
            buttons={
              <>
                <Button color="secondary" onClick={goBack}>
                  {intl.formatMessage(messages.cancel)}
                </Button>
                <Button color="primary" type="submit" id="update">
                  {intl.formatMessage(params.spaceId ? messages.update : messages.create)}
                </Button>
              </>
            }
            onSubmit={onSubmit}
            onReset={goBack}
            schema={schema}
          >
            <KeyValue>
              <KeyValue.Label>
                <FormattedMessage {...messages.spaceId} />
              </KeyValue.Label>
              <KeyValue.Value>{space.id}</KeyValue.Value>
            </KeyValue>
            <Text
              name="name"
              label={<FormattedMessage {...messages.spaceName} />}
              placeholder={intl.formatMessage(messages.spaceName)}
              required
            />
            <Text
              name="description"
              label={<FormattedMessage {...messages.description} />}
              placeholder={intl.formatMessage(messages.descriptionPlaceholder)}
            />
          </Form>
        )}
        {!!params.spaceId && (
          <SpaceAdmins
            addSpaceAdmin={props.actions.addSpaceAdmin}
            addUserToSpaceAsAdmin={props.actions.addUserToSpaceAsAdmin}
            fetchUsersPaginated={props.actions.fetchUsersPaginated}
            isSearching={props.isSearching}
            isFetchingAdmins={props.isFetchingAdmins}
            isAddingSpaceAdmin={props.isAddingSpaceAdmin}
            isRemovingSpaceAdmin={props.isRemovingSpaceAdmin}
            isSearchUsersComplete={props.isSearchUsersComplete}
            removeSpaceAdmin={props.actions.removeSpaceAdmin}
            searchResults={props.searchResults}
            space={props.space}
            spaceAdminRole={props.spaceAdminRole}
            spaceAdmins={props.spaceAdmins}
          />
        )}
      </>
    </Page>
  )
}

OrganizationSpaceEditPage.displayName = 'OrganizationSpaceEditPage'

OrganizationSpaceEditPage.propTypes = {
  actions: PropTypes.shape({
    addSpaceAndSpaceAdmin: PropTypes.func.isRequired,
    addSpaceAdmin: PropTypes.func.isRequired,
    addUserToSpaceAsAdmin: PropTypes.func.isRequired,
    fetchRoles: PropTypes.func.isRequired,
    fetchUsersPaginated: PropTypes.func.isRequired,
    fetchSpaceMembersInRole: PropTypes.func.isRequired,
    removeSpaceAdmin: PropTypes.func.isRequired,
    resetRequest: PropTypes.func.isRequired,
    updateSpace: PropTypes.func.isRequired
  }).isRequired,
  currentUser: PropTypes.object.isRequired,
  space: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    default: PropTypes.bool
  }),
  hasError: PropTypes.bool.isRequired,
  isAddSpaceComplete: PropTypes.bool.isRequired,
  isAddMemberToSpaceComplete: PropTypes.bool.isRequired,
  isAddSpaceAdminComplete: PropTypes.bool.isRequired,
  isSearchUsersComplete: PropTypes.bool.isRequired,
  isSearching: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
  isFetchingAdmins: PropTypes.bool.isRequired,
  isAddingSpaceAdmin: PropTypes.bool.isRequired,
  isRemovingSpaceAdmin: PropTypes.bool.isRequired,
  isUpdateComplete: PropTypes.bool.isRequired,
  isFetchSpaceAdminsComplete: PropTypes.bool.isRequired,
  searchResults: PropTypes.arrayOf(PropTypes.object),
  spaceAdmins: PropTypes.arrayOf(PropTypes.object),
  spaceAdminRole: PropTypes.object,
  spaces: PropTypes.array
}

OrganizationSpaceEditPage.defaultProps = {
  searchResults: null,
  space: {},
  spaces: [],
  spaceAdmins: [],
  spaceAdminRole: {}
}

const DisconnectedOrganizationSpaceEditPage = OrganizationSpaceEditPage
export { /** @deprecated */ DisconnectedOrganizationSpaceEditPage as OrganizationSpaceEditPage }

const makeMapStateToProps = () => {
  const getSpaceById = makeGetSpaceById()

  return (state: any, ownProps: any) => {
    return {
      currentUser: getCurrentUser(state),
      hasError: isRequestError(state, [
        ADD_SPACE,
        UPDATE_SPACE,
        ADD_MEMBER_TO_SPACE,
        ADD_SPACE_ADMIN,
        REMOVE_SPACE_ADMIN
      ]),
      isUpdateComplete: isRequestComplete(state, UPDATE_SPACE),
      isSearchUsersComplete: isRequestComplete(state, FETCH_ORG_ADMINS_TO_ADD),
      isAddSpaceComplete: isRequestComplete(state, ADD_SPACE),
      isAddMemberToSpaceComplete: isRequestComplete(state, ADD_MEMBER_TO_SPACE),
      isAddSpaceAdminComplete: isRequestComplete(state, ADD_SPACE_ADMIN),
      isFetchSpaceAdminsComplete: isRequestComplete(state, FETCH_SPACE_MEMBERS),
      isFetchingAdmins: isRequestPending(state, FETCH_SPACE_MEMBERS),
      isRemovingSpaceAdmin: isRequestPending(state, REMOVE_SPACE_ADMIN),
      isAddingSpaceAdmin: isRequestPending(state, [ADD_SPACE_ADMIN, ADD_MEMBER_TO_SPACE]),
      isSearching: isRequestPending(state, FETCH_ORG_ADMINS_TO_ADD),
      isSaving: isRequestPending(state, [ADD_SPACE, UPDATE_SPACE]),
      space: getSpaceById(state, ownProps.match.params.spaceId),
      spaces: getSpaces(state),
      spaceAdmins: getSpaceAdmins(state),
      spaceAdminRole: getSpaceAdminRole(state),
      searchResults: getSearchedUsersNotAlreadyAdmins(state)
    }
  }
}

function mapDispatchToProps(dispatch: any) {
  return {
    actions: bindActionCreators(
      {
        addSpaceAndSpaceAdmin,
        addSpaceAdmin,
        addUserToSpaceAsAdmin,
        fetchRoles,
        fetchSpaceMembersInRole,
        removeSpaceAdmin,
        fetchUsersPaginated,
        resetRequest,
        updateSpace
      },
      dispatch
    )
  }
}

export default withRouter(connect(makeMapStateToProps, mapDispatchToProps)(DisconnectedOrganizationSpaceEditPage))
